import { cn } from '@/lib/utils';
import { formatNotificationTime } from '@/lib/notifications/format-relative-time';
import type { NotificationResponseDto } from '@/lib/models';
import { NotificationIcon } from './NotificationIcon';

export interface NotificationItemProps {
  notification: NotificationResponseDto;
  /** Fired when the row is activated — opens the detail pop-up and marks it read. */
  onClick: () => void;
}

/** Pull a string field off the loosely-typed `meta` payload, if present. */
function metaString(meta: Record<string, unknown>, key: string): string | undefined {
  const value = meta[key];
  return typeof value === 'string' ? value : undefined;
}

/**
 * One row in the drawer: leading circle (market image / type icon), title, and
 * a right-aligned timestamp. Unread rows are tinted (light purple) with a red
 * dot top-right; read rows recede to a bordered white card.
 */
export function NotificationItem({ notification, onClick }: NotificationItemProps) {
  const { title, createdAt, read, type, meta } = notification;
  const imageUrl = metaString(meta, 'marketImageUrl');

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid="notification-item"
        data-unread={!read}
        className={cn(
          'relative flex w-full items-center gap-3 rounded-card px-4 py-3 text-left transition-colors',
          read ? 'border border-border bg-surface hover:bg-surface-muted' : 'bg-accent/10 hover:bg-accent/15',
        )}
      >
        <NotificationIcon type={type} imageUrl={imageUrl} />
        <span className="min-w-0 flex-1">
          <span className={cn('block truncate text-sm', read ? 'font-medium text-primary' : 'font-semibold text-primary')}>
            {title}
          </span>
        </span>
        <time className="shrink-0 text-xs text-muted" dateTime={createdAt}>
          {formatNotificationTime(createdAt)}
        </time>
        {!read && (
          <span
            data-testid="unread-dot"
            aria-label="Unread"
            className="absolute right-2 top-2 block size-3 rounded-full bg-danger ring-2 ring-surface"
          />
        )}
      </button>
    </li>
  );
}
